import clsx from "clsx"
import dayjs from "dayjs"
import L from "leaflet"
import { Marker, Polyline, Tooltip } from "react-leaflet"
import { shallowEqual } from "react-redux"

import { useAppDispatch, useAppSelector } from "@/App"
import { selectActiveOrdersByDriverId } from "@/store/orders"
import { setAboutPanel } from "@/store/ui"
import { Driver, Order, OrderPoint } from "@/types"
import { getDatetimeSortValue, OrderUtils, zIndexMapMarkers } from "@/utils"

export function sortTasksByEstimatedTime(a: OrderPoint, b: OrderPoint) {
  return getDatetimeSortValue(b.estimated_time, a.estimated_time)
}

export const DriverTasksRoute = ({ driverId }: { driverId: Driver["id"] }) => {
  const orders = useAppSelector(selectActiveOrdersByDriverId(driverId), shallowEqual)
  const aboutOrderPanel = useAppSelector((state) => state.ui.aboutPanel)

  const dispatch = useAppDispatch()

  const tasks = orders.flatMap(OrderUtils.getOrderPoints).sort(sortTasksByEstimatedTime)

  function getMarkerIndex(pointId: OrderPoint["id"]) {
    return tasks.findIndex((task) => task!.id === pointId) + 1
  }

  function toggleAboutPanel(orderId: Order["id"]) {
    dispatch(setAboutPanel(aboutOrderPanel && aboutOrderPanel.id === orderId ? false : { type: "order", id: orderId }))
  }

  if (!orders) return null

  return (
    <>
      {orders.map((order) => (
        <div key={`driver-task-route-markers-${order.id}`}>
          {order.pickup && (
            <Marker
              zIndexOffset={zIndexMapMarkers.driver_tasks_routes}
              icon={getMarkerIcon(order, `${getMarkerIndex(order.pickup.id)} <span>P</span>`)}
              position={[order.pickup.lat, order.pickup.lng]}
              onClick={() => toggleAboutPanel(order.id)}
            >
              <Tooltip direction="top" offset={[0, -20]}>
                Odbiór godzina {dayjs(order.pickup.estimated_time!).format("HH:mm")}
              </Tooltip>
            </Marker>
          )}

          {order.delivery && (
            <Marker
              zIndexOffset={zIndexMapMarkers.driver_tasks_routes}
              icon={getMarkerIcon(order, `${getMarkerIndex(order.delivery.id)} <span>D</span>`)}
              position={[order.delivery.lat, order.delivery.lng]}
              onClick={() => toggleAboutPanel(order.id)}
            >
              <Tooltip direction="top" offset={[0, -20]}>
                Doręczenie godzina {dayjs(order.delivery.estimated_time!).format("HH:mm")}
              </Tooltip>
            </Marker>
          )}
        </div>
      ))}

      {tasks.map((task, currentIndex, tasks) => {
        const nextTask = tasks[currentIndex + 1]
        if (!nextTask) return null

        return (
          <Polyline
            key={`driver-task-route-path-${task.id}`}
            className="driver-task-path"
            positions={[
              [task.lat, task.lng],
              [nextTask.lat, nextTask.lng],
            ]}
          />
        )
      })}
    </>
  )
}

function getMarkerIcon(order: Order, label: string) {
  return L.divIcon({
    html: label,
    className: clsx("driver-task-marker", OrderUtils.isInProgress(order) && "in-progress"),
    iconAnchor: [16, 16],
    popupAnchor: [0, -16],
    iconSize: [32, 32],
  })
}
